import React, { useState } from 'react';
import { Calculator, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';

// =============================================================================
// SHARED PRIMITIVES
// =============================================================================
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
      {hint && <span className="text-xs text-gray-400 ml-1">{hint}</span>}
    </label>
    {children}
  </div>
);

const NumberInput = ({ value, onChange, placeholder, step = '1', min, max, testId }) => (
  <input
    type="number"
    value={value}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    step={step}
    min={min}
    max={max}
    data-testid={testId}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500"
  />
);

const Select = ({ value, onChange, options, testId }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testId}
    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-raspberry-500"
  >
    <option value="">Select</option>
    {options.map((o) => (
      <option key={o.value} value={o.value}>{o.label}</option>
    ))}
  </select>
);

const Checkbox = ({ label, checked, onChange, testId }) => (
  <label className="flex items-start gap-2 cursor-pointer py-1">
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      data-testid={testId}
      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-raspberry-700 focus:ring-raspberry-500"
    />
    <span className="text-sm text-gray-700 leading-snug">{label}</span>
  </label>
);

const Btn = ({ onClick, children, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="w-full py-2 bg-raspberry-700 text-white rounded-lg hover:bg-raspberry-800 font-medium transition-colors"
  >
    {children}
  </button>
);

const TONE = {
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700' },
  yellow: { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700' },
  gray: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' },
};

const ResultCard = ({ tone = 'gray', children }) => {
  const t = TONE[tone] || TONE.gray;
  return (
    <div className={`p-4 ${t.bg} border ${t.border} rounded-lg space-y-2`} data-testid="calc-result">
      {children}
    </div>
  );
};

const KV = ({ k, v, tone }) => {
  const color = tone ? TONE[tone].text : 'text-gray-800';
  return (
    <div className="flex justify-between items-center gap-3">
      <span className="text-gray-600 text-sm">{k}</span>
      <span className={`font-semibold ${color} text-right`}>{v}</span>
    </div>
  );
};

const Note = ({ children }) => (
  <div className="p-2 bg-white rounded border border-gray-200">
    <p className="text-sm text-gray-700">{children}</p>
  </div>
);

// "Send to Chat" CTA — drops the formatted clinical note into the chat input
const SendBtn = ({ note, onSend }) => {
  if (!note || !onSend) return null;
  return (
    <button
      type="button"
      onClick={() => onSend(note)}
      data-testid="calc-send-to-chat"
      className="w-full mt-2 inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-gradient-to-r from-raspberry-700 to-[#7a0e2e] text-white text-sm font-semibold hover:opacity-95 transition-opacity shadow-md"
    >
      <Send className="w-4 h-4" />
      Send result to chat
    </button>
  );
};

// =============================================================================
// 1. EDD
// =============================================================================
const EDDCalc = ({ onSend }) => {
  const [lmp, setLmp] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!lmp) return;
    const lmpDate = new Date(lmp);
    const edd = new Date(lmpDate); edd.setDate(edd.getDate() + 280);
    const diffDays = Math.floor((new Date() - lmpDate) / (1000 * 60 * 60 * 24));
    const weeks = Math.floor(diffDays / 7); const days = diffDays % 7;
    const r = {
      edd: edd.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }),
      ga: `${weeks} weeks ${days} days`,
      trimester: weeks < 13 ? 'First' : weeks < 27 ? 'Second' : 'Third',
    };
    r.note = `**EDD Calculation**\n- LMP: ${new Date(lmp).toLocaleDateString('en-IN')}\n- Expected Due Date: ${r.edd}\n- Current Gestational Age: ${r.ga}\n- Trimester: ${r.trimester}`;
    setResult(r);
  };

  return (
    <div className="space-y-4">
      <Field label="Last Menstrual Period (LMP)">
        <input type="date" value={lmp} onChange={(e) => setLmp(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-raspberry-500" />
      </Field>
      <Btn onClick={calculate}>Calculate EDD</Btn>
      {result && (
        <>
          <ResultCard tone="green">
            <KV k="Expected Due Date" v={result.edd} tone="green" />
            <KV k="Gestational Age" v={result.ga} tone="green" />
            <KV k="Trimester" v={result.trimester} tone="green" />
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 2. BMI
// =============================================================================
const BMICalc = ({ onSend }) => {
  const [weight, setWeight] = useState(''); const [height, setHeight] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!weight || !height) return;
    const h = parseFloat(height) / 100;
    const bmi = parseFloat(weight) / (h * h);
    let category, tone, impact;
    if (bmi < 18.5) { category = 'Underweight'; tone = 'yellow'; impact = 'May cause irregular cycles, anovulation. Weight gain advised pre-conception.'; }
    else if (bmi < 25) { category = 'Normal'; tone = 'green'; impact = 'Optimal for conception. Good pregnancy outcomes expected.'; }
    else if (bmi < 30) { category = 'Overweight'; tone = 'orange'; impact = 'May affect ovulation. Higher risk of GDM, preeclampsia.'; }
    else { category = 'Obese'; tone = 'red'; impact = 'Significantly affects fertility. High-risk pregnancy. Weight management essential.'; }
    const r = { bmi: bmi.toFixed(1), category, tone, impact };
    r.note = `**BMI Calculation**\n- Weight: ${weight} kg · Height: ${height} cm\n- BMI: ${r.bmi} kg/m²\n- Category: ${category}\n- Fertility impact: ${impact}`;
    setResult(r);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Weight (kg)"><NumberInput value={weight} onChange={setWeight} placeholder="70" /></Field>
        <Field label="Height (cm)"><NumberInput value={height} onChange={setHeight} placeholder="165" /></Field>
      </div>
      <Btn onClick={calculate}>Calculate BMI</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="BMI" v={result.bmi} tone={result.tone} />
            <KV k="Category" v={result.category} tone={result.tone} />
            <Note>{result.impact}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 3. BISHOP SCORE
// =============================================================================
const BishopCalc = ({ onSend }) => {
  const [d, setD] = useState(''); const [e, setE] = useState(''); const [s, setS] = useState('');
  const [c, setC] = useState(''); const [p, setP] = useState(''); const [result, setResult] = useState(null);

  const calculate = () => {
    let score = 0;
    const dv = parseInt(d); if (dv >= 5) score += 3; else if (dv >= 3) score += 2; else if (dv >= 1) score += 1;
    const ev = parseInt(e); if (ev >= 80) score += 3; else if (ev >= 60) score += 2; else if (ev >= 40) score += 1;
    const sv = parseInt(s); if (sv >= 1) score += 3; else if (sv >= 0) score += 2; else if (sv >= -1) score += 1;
    if (c === 'soft') score += 2; else if (c === 'medium') score += 1;
    if (p === 'anterior') score += 2; else if (p === 'mid') score += 1;
    let interp, tone;
    if (score >= 8) { interp = 'Favorable cervix — high induction success rate'; tone = 'green'; }
    else if (score >= 6) { interp = 'Moderately favorable — consider ripening agents'; tone = 'yellow'; }
    else { interp = 'Unfavorable cervix — cervical ripening recommended before induction'; tone = 'red'; }
    const r = { score, interp, tone };
    r.note = `**Bishop Score**\n- Dilation: ${d} cm · Effacement: ${e}% · Station: ${s}\n- Consistency: ${c} · Position: ${p}\n- **Score: ${score}/13** → ${interp}`;
    setResult(r);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Dilation (cm)"><NumberInput value={d} onChange={setD} placeholder="0-10" min="0" max="10" /></Field>
        <Field label="Effacement (%)"><NumberInput value={e} onChange={setE} placeholder="0-100" min="0" max="100" /></Field>
        <Field label="Station">
          <Select value={s} onChange={setS} options={[
            { value: '-3', label: '-3' }, { value: '-2', label: '-2' }, { value: '-1', label: '-1' },
            { value: '0', label: '0' }, { value: '1', label: '+1' }, { value: '2', label: '+2' }, { value: '3', label: '+3' },
          ]} />
        </Field>
        <Field label="Consistency">
          <Select value={c} onChange={setC} options={[
            { value: 'firm', label: 'Firm' }, { value: 'medium', label: 'Medium' }, { value: 'soft', label: 'Soft' },
          ]} />
        </Field>
        <div className="col-span-2">
          <Field label="Position">
            <Select value={p} onChange={setP} options={[
              { value: 'posterior', label: 'Posterior' }, { value: 'mid', label: 'Mid' }, { value: 'anterior', label: 'Anterior' },
            ]} />
          </Field>
        </div>
      </div>
      <Btn onClick={calculate}>Calculate Bishop Score</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Bishop Score" v={`${result.score}/13`} tone={result.tone} />
            <Note>{result.interp}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 4. FETAL WEIGHT (Hadlock)
// =============================================================================
const FetalWeightCalc = ({ onSend }) => {
  const [bpd, setBpd] = useState(''); const [ac, setAc] = useState(''); const [fl, setFl] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (!ac || !fl) return;
    const a = parseFloat(ac) / 10; const f = parseFloat(fl) / 10;
    const logWt = 1.304 + 0.05281 * a + 0.1938 * f - 0.004 * a * f;
    const wt = Math.pow(10, logWt);
    const r = {
      weight: Math.round(wt),
      lbs: (wt / 453.592).toFixed(2),
      note: '',
    };
    r.note = `**Estimated Fetal Weight (Hadlock)**\n- BPD: ${bpd} mm · AC: ${ac} mm · FL: ${fl} mm\n- **EFW: ${r.weight} g (${r.lbs} lbs)**\n- Compare with standard growth charts for percentile.`;
    setResult(r);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Field label="BPD (mm)"><NumberInput value={bpd} onChange={setBpd} placeholder="85" /></Field>
        <Field label="AC (mm)"><NumberInput value={ac} onChange={setAc} placeholder="280" /></Field>
        <Field label="FL (mm)"><NumberInput value={fl} onChange={setFl} placeholder="60" /></Field>
      </div>
      <Btn onClick={calculate}>Calculate Fetal Weight</Btn>
      {result && (
        <>
          <ResultCard tone="blue">
            <KV k="Estimated Fetal Weight" v={`${result.weight} g  (${result.lbs} lbs)`} tone="blue" />
            <Note>Hadlock formula (BPD+AC+FL). Compare with standard growth charts for percentile.</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 5. AMH
// =============================================================================
const AMHCalc = ({ onSend }) => {
  const [amh, setAmh] = useState(''); const [age, setAge] = useState(''); const [result, setResult] = useState(null);

  const calculate = () => {
    if (!amh || !age) return;
    const v = parseFloat(amh); const a = parseInt(age);
    let reserve, tone, prog, rec;
    if (v < 0.5) { reserve = 'Very Low / Diminished'; tone = 'red'; prog = 'Significantly reduced reserve. Poor response to stimulation expected.'; rec = 'Consider donor oocytes, aggressive protocols, time-sensitive treatment.'; }
    else if (v < 1.0) { reserve = 'Low'; tone = 'orange'; prog = 'Reduced reserve. Suboptimal stimulation response likely.'; rec = 'Early fertility treatment. Higher gonadotropin doses.'; }
    else if (v < 3.5) { reserve = 'Normal'; tone = 'green'; prog = 'Normal reserve for age. Good ART prognosis.'; rec = 'Standard protocols appropriate.'; }
    else { reserve = 'High'; tone = 'blue'; prog = 'High reserve — OHSS risk with stimulation.'; rec = 'Lower gonadotropin doses, antagonist protocol, monitor for OHSS.'; }
    let ageNote = '';
    if (a > 40 && v > 1) ageNote = 'Note: despite reasonable AMH, egg quality declines significantly after 40.';
    else if (a < 35 && v < 1) ageNote = 'Low AMH at young age suggests premature ovarian insufficiency. Workup advised.';
    const r = { reserve, tone, prog, rec, ageNote };
    r.note = `**AMH Interpretation**\n- AMH: ${v} ng/mL · Age: ${a} yrs\n- Ovarian reserve: **${reserve}**\n- Prognosis: ${prog}\n- Recommendation: ${rec}${ageNote ? `\n- ${ageNote}` : ''}`;
    setResult(r);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="AMH (ng/mL)"><NumberInput value={amh} onChange={setAmh} placeholder="2.5" step="0.1" /></Field>
        <Field label="Age (years)"><NumberInput value={age} onChange={setAge} placeholder="32" /></Field>
      </div>
      <Btn onClick={calculate}>Interpret AMH</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Ovarian Reserve" v={result.reserve} tone={result.tone} />
            <Note><b>Prognosis:</b> {result.prog}</Note>
            <Note><b>Recommendation:</b> {result.rec}</Note>
            {result.ageNote && <div className="p-2 bg-yellow-50 rounded border border-yellow-200 text-sm text-yellow-800">{result.ageNote}</div>}
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 6. APGAR
// =============================================================================
const APGARCalc = ({ onSend }) => {
  const [a, setA] = useState(''); const [p, setP] = useState(''); const [g, setG] = useState('');
  const [act, setAct] = useState(''); const [r, setR] = useState(''); const [result, setResult] = useState(null);

  const calculate = () => {
    const score = [a, p, g, act, r].map((v) => parseInt(v) || 0).reduce((x, y) => x + y, 0);
    let interp, tone;
    if (score >= 7) { interp = 'Normal — routine care'; tone = 'green'; }
    else if (score >= 4) { interp = 'Moderately depressed — stimulation, oxygen, warmth'; tone = 'yellow'; }
    else { interp = 'Severely depressed — immediate resuscitation required'; tone = 'red'; }
    const res = { score, interp, tone };
    res.note = `**APGAR Score**\n- Appearance: ${a} · Pulse: ${p} · Grimace: ${g} · Activity: ${act} · Respiration: ${r}\n- **Total: ${score}/10** → ${interp}`;
    setResult(res);
  };

  const opts = [{ value: '0', label: '0' }, { value: '1', label: '1' }, { value: '2', label: '2' }];
  return (
    <div className="space-y-3">
      <Field label="Appearance (Color)" hint="0=Blue · 1=Body pink · 2=All pink"><Select value={a} onChange={setA} options={opts} /></Field>
      <Field label="Pulse" hint="0=Absent · 1=<100 · 2=≥100"><Select value={p} onChange={setP} options={opts} /></Field>
      <Field label="Grimace (Reflex)" hint="0=None · 1=Grimace · 2=Cry"><Select value={g} onChange={setG} options={opts} /></Field>
      <Field label="Activity (Tone)" hint="0=Limp · 1=Some · 2=Active"><Select value={act} onChange={setAct} options={opts} /></Field>
      <Field label="Respiration" hint="0=Absent · 1=Weak · 2=Strong"><Select value={r} onChange={setR} options={opts} /></Field>
      <Btn onClick={calculate}>Calculate APGAR</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="APGAR Score" v={`${result.score}/10`} tone={result.tone} />
            <Note>{result.interp}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 7. PREECLAMPSIA / ASPIRIN
// =============================================================================
const PreeclampsiaCalc = ({ onSend }) => {
  const [high, setHigh] = useState({ priorPE: false, multifetal: false, cHTN: false, dm: false, ckd: false, autoimmune: false });
  const [mod, setMod] = useState({ nullip: false, obesity: false, age35: false, family: false, sga: false, ivf: false, interval: false });
  const [result, setResult] = useState(null);
  const t = (set, k) => (v) => set((p) => ({ ...p, [k]: v }));

  const calculate = () => {
    const highN = Object.values(high).filter(Boolean).length;
    const modN = Object.values(mod).filter(Boolean).length;
    let rec, tone;
    if (highN >= 1) { rec = 'Aspirin RECOMMENDED — start 81–162 mg PO daily at 12–28 weeks (ideally <16 wk); continue until delivery.'; tone = 'red'; }
    else if (modN >= 2) { rec = 'Aspirin RECOMMENDED (≥2 moderate factors) — 81–162 mg PO daily, 12–28 wk → delivery.'; tone = 'orange'; }
    else if (modN === 1) { rec = 'Aspirin may be CONSIDERED on shared decision-making basis.'; tone = 'yellow'; }
    else { rec = 'Aspirin NOT routinely indicated. Monitor BP at every visit; PE prevention via lifestyle.'; tone = 'green'; }
    const note = `**Preeclampsia Risk Assessment (ACOG/USPSTF)**\n- High-risk factors: ${highN}\n- Moderate-risk factors: ${modN}\n- **Recommendation:** ${rec}`;
    setResult({ highN, modN, rec, tone, note });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold text-red-700 uppercase mb-1">High-risk factors (≥1 → start aspirin)</p>
        <Checkbox label="Prior preeclampsia" checked={high.priorPE} onChange={t(setHigh, 'priorPE')} />
        <Checkbox label="Multifetal gestation" checked={high.multifetal} onChange={t(setHigh, 'multifetal')} />
        <Checkbox label="Chronic hypertension" checked={high.cHTN} onChange={t(setHigh, 'cHTN')} />
        <Checkbox label="Pregestational T1/T2 diabetes" checked={high.dm} onChange={t(setHigh, 'dm')} />
        <Checkbox label="Chronic kidney disease" checked={high.ckd} onChange={t(setHigh, 'ckd')} />
        <Checkbox label="Autoimmune (SLE, APLA)" checked={high.autoimmune} onChange={t(setHigh, 'autoimmune')} />
      </div>
      <div>
        <p className="text-xs font-semibold text-orange-700 uppercase mb-1">Moderate-risk factors (≥2 → start aspirin)</p>
        <Checkbox label="Nulliparity" checked={mod.nullip} onChange={t(setMod, 'nullip')} />
        <Checkbox label="Obesity (BMI > 30)" checked={mod.obesity} onChange={t(setMod, 'obesity')} />
        <Checkbox label="Age ≥ 35 years" checked={mod.age35} onChange={t(setMod, 'age35')} />
        <Checkbox label="Family history of preeclampsia" checked={mod.family} onChange={t(setMod, 'family')} />
        <Checkbox label="Prior SGA / adverse pregnancy outcome" checked={mod.sga} onChange={t(setMod, 'sga')} />
        <Checkbox label="IVF conception" checked={mod.ivf} onChange={t(setMod, 'ivf')} />
        <Checkbox label="Interpregnancy interval >10 yrs" checked={mod.interval} onChange={t(setMod, 'interval')} />
      </div>
      <Btn onClick={calculate}>Assess Risk</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="High-risk factors" v={result.highN} tone={result.tone} />
            <KV k="Moderate-risk factors" v={result.modN} tone={result.tone} />
            <Note>{result.rec}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 8. VBAC
// =============================================================================
const VBACCalc = ({ onSend }) => {
  const [age, setAge] = useState(''); const [bmi, setBmi] = useState('');
  const [priorVD, setPriorVD] = useState(false); const [priorVBAC, setPriorVBAC] = useState(false);
  const [recurrent, setRecurrent] = useState(false); const [result, setResult] = useState(null);

  const calculate = () => {
    let p = 0.65;
    const a = parseFloat(age) || 30; const b = parseFloat(bmi) || 25;
    p -= Math.max(0, (a - 30)) * 0.005;
    p -= Math.max(0, (b - 25)) * 0.01;
    if (priorVD) p += 0.10;
    if (priorVBAC) p += 0.15;
    if (recurrent) p -= 0.12;
    const pct = Math.max(5, Math.min(95, Math.round(p * 100)));
    let tone, interp;
    if (pct >= 70) { tone = 'green'; interp = 'High likelihood of successful VBAC — TOLAC is a strong option.'; }
    else if (pct >= 50) { tone = 'yellow'; interp = 'Moderate success likelihood — counsel on TOLAC vs ERCD trade-offs.'; }
    else { tone = 'red'; interp = 'Low success likelihood — repeat cesarean may be preferable; shared decision.'; }
    const note = `**VBAC Success Estimate (Grobman/MFMU simplified)**\n- Age: ${a} · BMI: ${b}\n- Prior vaginal delivery: ${priorVD ? 'Yes' : 'No'} · Prior VBAC: ${priorVBAC ? 'Yes' : 'No'} · Recurrent indication: ${recurrent ? 'Yes' : 'No'}\n- **Predicted success: ${pct}%** → ${interp}`;
    setResult({ pct, tone, interp, note });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Maternal age"><NumberInput value={age} onChange={setAge} placeholder="30" /></Field>
        <Field label="BMI"><NumberInput value={bmi} onChange={setBmi} placeholder="25" step="0.1" /></Field>
      </div>
      <Checkbox label="Prior vaginal delivery (any)" checked={priorVD} onChange={setPriorVD} />
      <Checkbox label="Prior successful VBAC" checked={priorVBAC} onChange={setPriorVBAC} />
      <Checkbox label="Recurrent indication (arrest of dilation/descent)" checked={recurrent} onChange={setRecurrent} />
      <Btn onClick={calculate}>Estimate VBAC Success</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Predicted VBAC success" v={`${result.pct}%`} tone={result.tone} />
            <Note>{result.interp} Simplified from Grobman/MFMU — confirm with full nomogram for borderline cases.</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 9. PPH RISK
// =============================================================================
const PPHRiskCalc = ({ onSend }) => {
  const [med, setMed] = useState({ priorCS: false, multipara4: false, fibroids: false, chorio: false, mgso4: false });
  const [high, setHigh] = useState({ placentaPrev: false, accreta: false, hct30: false, plt: false, bleeding: false, abnPlac: false });
  const [result, setResult] = useState(null);
  const t = (set, k) => (v) => set((p) => ({ ...p, [k]: v }));

  const calculate = () => {
    const h = Object.values(high).filter(Boolean).length;
    const m = Object.values(med).filter(Boolean).length;
    let tier, tone, plan;
    if (h >= 1) { tier = 'HIGH RISK'; tone = 'red'; plan = 'Type & cross-match 2 units PRBC. IV access ×2 large-bore. Active management of 3rd stage. Anaesthesia + senior obstetrician on standby. Uterotonics + balloon tamponade ready.'; }
    else if (m >= 1) { tier = 'MEDIUM RISK'; tone = 'orange'; plan = 'Type & screen. IV access. Active management of 3rd stage. Uterotonics ready. Monitor for first 2 hrs postpartum.'; }
    else { tier = 'LOW RISK'; tone = 'green'; plan = 'Routine active management of 3rd stage with oxytocin 10 IU IM. Routine postpartum monitoring.'; }
    const note = `**PPH Risk Stratification (CMQCC tiered)**\n- High-risk factors: ${h} · Medium-risk factors: ${m}\n- **Tier: ${tier}**\n- Plan: ${plan}`;
    setResult({ tier, tone, plan, note });
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-red-700 uppercase mb-1">HIGH-risk factors (any ONE → high)</p>
        <Checkbox label="Placenta previa / low-lying" checked={high.placentaPrev} onChange={t(setHigh, 'placentaPrev')} />
        <Checkbox label="Suspected accreta spectrum (PAS)" checked={high.accreta} onChange={t(setHigh, 'accreta')} />
        <Checkbox label="Hct < 30 or anemia" checked={high.hct30} onChange={t(setHigh, 'hct30')} />
        <Checkbox label="Platelets < 100k" checked={high.plt} onChange={t(setHigh, 'plt')} />
        <Checkbox label="Active bleeding on admission" checked={high.bleeding} onChange={t(setHigh, 'bleeding')} />
        <Checkbox label="Known coagulopathy" checked={high.abnPlac} onChange={t(setHigh, 'abnPlac')} />
      </div>
      <div>
        <p className="text-xs font-semibold text-orange-700 uppercase mb-1">MEDIUM-risk factors</p>
        <Checkbox label="Prior cesarean / uterine surgery" checked={med.priorCS} onChange={t(setMed, 'priorCS')} />
        <Checkbox label="Multiparity ≥ 4" checked={med.multipara4} onChange={t(setMed, 'multipara4')} />
        <Checkbox label="Large fibroids / over-distended uterus" checked={med.fibroids} onChange={t(setMed, 'fibroids')} />
        <Checkbox label="Chorioamnionitis" checked={med.chorio} onChange={t(setMed, 'chorio')} />
        <Checkbox label="Magnesium sulfate use" checked={med.mgso4} onChange={t(setMed, 'mgso4')} />
      </div>
      <Btn onClick={calculate}>Stratify PPH Risk</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Risk Tier" v={result.tier} tone={result.tone} />
            <Note>{result.plan}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 10. MgSO4
// =============================================================================
const MgSO4Calc = ({ onSend }) => {
  const [regimen, setRegimen] = useState('pritchard');
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    let load, maint;
    if (regimen === 'pritchard') {
      load = 'Loading: 4 g IV slow (over 5–10 min as 20% solution) + 10 g IM deep (5 g into each buttock with 1 mL 2% lignocaine).';
      maint = 'Maintenance: 5 g IM deep alternate buttocks every 4 hours × 24 hours after delivery / last fit.';
    } else {
      load = 'Loading: 4–6 g IV over 15–20 min (diluted in 100 mL NS).';
      maint = 'Maintenance: 1–2 g/hr IV infusion × 24 hours after delivery / last fit.';
    }
    const monitor = 'Monitor every hour: knee jerk present, RR > 12/min, urine output ≥ 30 mL/hr, SpO₂ > 95%. Antidote: Calcium gluconate 1 g (10 mL of 10%) IV slow over 10 min if toxicity.';
    const note = `**MgSO₄ Regimen — ${regimen === 'pritchard' ? 'Pritchard (IV+IM)' : 'Zuspan (IV infusion)'}**${weight ? `\n- Weight: ${weight} kg` : ''}\n- ${load}\n- ${maint}\n- ${monitor}`;
    setResult({ load, maint, monitor, note });
  };

  return (
    <div className="space-y-4">
      <Field label="Regimen">
        <Select value={regimen} onChange={setRegimen} options={[
          { value: 'pritchard', label: 'Pritchard (IV + IM) — Indian standard' },
          { value: 'zuspan', label: 'Zuspan (IV infusion)' },
        ]} />
      </Field>
      <Field label="Patient weight (kg, optional)"><NumberInput value={weight} onChange={setWeight} placeholder="60" /></Field>
      <Btn onClick={calculate}>Generate Regimen</Btn>
      {result && (
        <>
          <ResultCard tone="red">
            <Note><b>Loading dose:</b> {result.load}</Note>
            <Note><b>Maintenance:</b> {result.maint}</Note>
            <Note><b>Monitoring & antidote:</b> {result.monitor}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 11. ANTI-D
// =============================================================================
const AntiDCalc = ({ onSend }) => {
  const [mode, setMode] = useState('event');
  const [event, setEvent] = useState('');
  const [kb, setKb] = useState('');
  const [vol, setVol] = useState('5000');
  const [result, setResult] = useState(null);

  const calculate = () => {
    if (mode === 'event') {
      let dose, eventLabel;
      if (event === 'firstTri') { dose = '50–150 mcg IM'; eventLabel = '1st-trimester loss / MTP / ectopic'; }
      else if (event === '28wk') { dose = '300 mcg IM'; eventLabel = 'Routine antenatal prophylaxis at 28 wk'; }
      else if (event === 'delivery') { dose = '300 mcg IM (within 72 hrs)'; eventLabel = 'After delivery (baby Rh+)'; }
      else if (event === 'abruption') { dose = '300 mcg IM + send KB (may need more — 300 mcg per 30 mL fetal blood)'; eventLabel = 'Abruption / large fetomaternal hemorrhage'; }
      else if (event === 'amnio') { dose = '300 mcg IM'; eventLabel = 'Amnio / CVS / ECV / abdominal trauma'; }
      else { return; }
      const note = `**Anti-D Immunoprophylaxis**\n- Indication: ${eventLabel}\n- **Dose: ${dose}**\n- Confirm Rh status, DAT, and consent. Document lot number.`;
      setResult({ dose, note });
    } else {
      const k = parseFloat(kb); const v = parseFloat(vol) || 5000;
      if (!k || k <= 0) return;
      const fetalMl = (k / 100) * v;
      const vials = Math.ceil(fetalMl / 30) + 1;
      const totalMcg = vials * 300;
      const dose = `${vials} vial(s) × 300 mcg = ${totalMcg} mcg IM`;
      const note = `**Anti-D Dose (Kleihauer-Betke based)**\n- KB: ${k}% · Maternal blood volume: ${v} mL\n- Estimated fetal blood: ${fetalMl.toFixed(1)} mL\n- **Dose: ${dose}**`;
      setResult({ dose, fetalMl: fetalMl.toFixed(1), note });
    }
  };

  return (
    <div className="space-y-4">
      <Field label="Calculation mode">
        <Select value={mode} onChange={setMode} options={[
          { value: 'event', label: 'By clinical event' },
          { value: 'kb', label: 'By Kleihauer-Betke %' },
        ]} />
      </Field>
      {mode === 'event' ? (
        <Field label="Event">
          <Select value={event} onChange={setEvent} options={[
            { value: 'firstTri', label: '1st-trimester loss / MTP / ectopic' },
            { value: '28wk', label: 'Routine antenatal at 28 wk' },
            { value: 'delivery', label: 'After delivery (baby Rh+)' },
            { value: 'abruption', label: 'Abruption / large fetomaternal hemorrhage' },
            { value: 'amnio', label: 'Amnio / CVS / ECV / trauma' },
          ]} />
        </Field>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <Field label="KB % fetal cells"><NumberInput value={kb} onChange={setKb} placeholder="0.5" step="0.01" /></Field>
          <Field label="Maternal blood vol (mL)"><NumberInput value={vol} onChange={setVol} placeholder="5000" /></Field>
        </div>
      )}
      <Btn onClick={calculate}>Calculate Anti-D Dose</Btn>
      {result && (
        <>
          <ResultCard tone="blue">
            <KV k="Dose" v={result.dose} tone="blue" />
            {result.fetalMl && <KV k="Estimated fetal blood" v={`${result.fetalMl} mL`} tone="blue" />}
            <Note>Confirm Rh status, DAT, and consent. Document lot number.</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 12. GDM INSULIN
// =============================================================================
const GDMInsulinCalc = ({ onSend }) => {
  const [weight, setWeight] = useState(''); const [ga, setGa] = useState('');
  const [pattern, setPattern] = useState('mixed'); const [result, setResult] = useState(null);

  const calculate = () => {
    const w = parseFloat(weight); if (!w) return;
    let perKg;
    const g = parseInt(ga) || 24;
    if (g < 18) perKg = 0.7; else if (g < 26) perKg = 0.8; else if (g < 36) perKg = 0.9; else perKg = 1.0;
    const total = Math.round(w * perKg);
    let plan, patternLabel;
    if (pattern === 'fasting') { patternLabel = 'High fasting only'; plan = `Start: Basal insulin (NPH or detemir) ${Math.round(total * 0.5)} units at bedtime. Titrate by 2 units every 3 days till FBG < 95.`; }
    else if (pattern === 'pp') { patternLabel = 'High postprandial only'; plan = `Start: Rapid-acting insulin (lispro/aspart) ${Math.round(total / 3 / 2)} units before EACH meal. Target 1-hr PPBG < 140 or 2-hr < 120.`; }
    else { patternLabel = 'Mixed pattern'; plan = `Start: Premixed 70/30 — ${Math.round(total * 0.67)} units pre-breakfast + ${Math.round(total * 0.33)} units pre-dinner. Or basal-bolus: ${Math.round(total * 0.5)} units basal HS + ${Math.round(total * 0.17)} units before each meal.`; }
    const note = `**GDM Insulin Starting Dose**\n- Weight: ${w} kg · GA: ${g} wk · Pattern: ${patternLabel}\n- **TDD: ${total} units**\n- ${plan}\n- Targets: FBG < 95, 1-hr PPBG < 140, 2-hr PPBG < 120. Review SMBG in 3–5 days.`;
    setResult({ total, plan, note });
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Patient weight (kg)"><NumberInput value={weight} onChange={setWeight} placeholder="65" /></Field>
        <Field label="Gestational age (weeks)"><NumberInput value={ga} onChange={setGa} placeholder="28" /></Field>
      </div>
      <Field label="Glucose pattern">
        <Select value={pattern} onChange={setPattern} options={[
          { value: 'fasting', label: 'High fasting only → basal' },
          { value: 'pp', label: 'High postprandial only → bolus' },
          { value: 'mixed', label: 'Mixed (most common) → premixed or basal-bolus' },
        ]} />
      </Field>
      <Btn onClick={calculate}>Calculate Starting Dose</Btn>
      {result && (
        <>
          <ResultCard tone="orange">
            <KV k="Total daily insulin (TDD)" v={`${result.total} units`} tone="orange" />
            <Note>{result.plan}</Note>
            <Note>Targets: FBG &lt; 95, 1-hr PPBG &lt; 140, 2-hr PPBG &lt; 120. Review SMBG diary in 3–5 days.</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 13. RMI
// =============================================================================
const RMICalc = ({ onSend }) => {
  const [usg, setUsg] = useState({ multiloc: false, solid: false, bilat: false, ascites: false, mets: false });
  const [meno, setMeno] = useState('pre');
  const [ca125, setCa125] = useState('');
  const [result, setResult] = useState(null);
  const t = (k) => (v) => setUsg((p) => ({ ...p, [k]: v }));

  const calculate = () => {
    const features = Object.values(usg).filter(Boolean).length;
    const U = features === 0 ? 0 : features === 1 ? 1 : 3;
    const M = meno === 'post' ? 3 : 1;
    const C = parseFloat(ca125) || 0;
    const rmi = U * M * C;
    let tone, interp;
    if (rmi >= 200) { tone = 'red'; interp = 'HIGH RISK of malignancy — refer to gynaecologic oncologist. Consider CT, tumor markers, MDT.'; }
    else if (rmi >= 25) { tone = 'orange'; interp = 'MODERATE risk — further imaging (MRI) + tumor marker panel. Discuss in tumor board.'; }
    else { tone = 'green'; interp = 'LOW risk — likely benign. Conservative management with serial USG; surgery if symptomatic.'; }
    const note = `**RMI — Risk of Malignancy Index (Adnexal Mass)**\n- USG features: ${features} → U = ${U}\n- Menopausal status: ${meno === 'post' ? 'Postmenopausal' : 'Premenopausal'} → M = ${M}\n- CA-125: ${C} U/mL\n- **RMI = ${U} × ${M} × ${C} = ${Math.round(rmi)}**\n- ${interp}`;
    setResult({ U, M, C, rmi, tone, interp, features, note });
  };

  return (
    <div className="space-y-3">
      <div>
        <p className="text-xs font-semibold text-gray-700 uppercase mb-1">Ultrasound features</p>
        <Checkbox label="Multilocular cyst" checked={usg.multiloc} onChange={t('multiloc')} />
        <Checkbox label="Solid areas / papillary projections" checked={usg.solid} onChange={t('solid')} />
        <Checkbox label="Bilateral lesions" checked={usg.bilat} onChange={t('bilat')} />
        <Checkbox label="Ascites" checked={usg.ascites} onChange={t('ascites')} />
        <Checkbox label="Intra-abdominal metastases" checked={usg.mets} onChange={t('mets')} />
      </div>
      <Field label="Menopausal status">
        <Select value={meno} onChange={setMeno} options={[
          { value: 'pre', label: 'Premenopausal (M = 1)' },
          { value: 'post', label: 'Postmenopausal (M = 3)' },
        ]} />
      </Field>
      <Field label="CA-125 (U/mL)"><NumberInput value={ca125} onChange={setCa125} placeholder="35" step="0.1" /></Field>
      <Btn onClick={calculate}>Calculate RMI</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="USG score (U)" v={`${result.U} (${result.features} feature${result.features === 1 ? '' : 's'})`} tone={result.tone} />
            <KV k="Menopausal score (M)" v={result.M} tone={result.tone} />
            <KV k="CA-125" v={`${result.C} U/mL`} tone={result.tone} />
            <KV k="RMI = U × M × CA-125" v={Math.round(result.rmi)} tone={result.tone} />
            <Note>{result.interp}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 14. ASCCP
// =============================================================================
const ASCCPCalc = ({ onSend }) => {
  const [hpv, setHpv] = useState(''); const [cyto, setCyto] = useState(''); const [age, setAge] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const a = parseInt(age) || 30;
    let mgmt, tone;
    const young = a < 25;
    if (hpv === 'pos16' || hpv === 'pos1618') { mgmt = 'HPV 16/18 positive — Colposcopy regardless of cytology. Consider expedited treatment if HSIL+ cytology.'; tone = 'red'; }
    else if (hpv === 'posOther' && (cyto === 'hsil' || cyto === 'agc')) { mgmt = 'HPV other high-risk + HSIL/AGC — Immediate colposcopy. Expedited treatment may be reasonable for HSIL.'; tone = 'red'; }
    else if (hpv === 'posOther' && (cyto === 'lsil' || cyto === 'ascus')) { mgmt = young ? 'Repeat co-test at 1 year (age <25 — observation preferred).' : 'Colposcopy.'; tone = 'orange'; }
    else if (hpv === 'posOther' && cyto === 'nilm') { mgmt = 'Repeat co-test in 1 year. Colposcopy if persistent at 1 yr.'; tone = 'yellow'; }
    else if (hpv === 'neg' && cyto === 'nilm') { mgmt = 'Routine screening — repeat co-test in 5 years.'; tone = 'green'; }
    else if (hpv === 'neg' && (cyto === 'ascus' || cyto === 'lsil')) { mgmt = 'Repeat co-test in 3 years.'; tone = 'yellow'; }
    else if (hpv === 'neg' && (cyto === 'hsil' || cyto === 'agc')) { mgmt = 'Colposcopy despite negative HPV (high-grade cytology).'; tone = 'orange'; }
    else { mgmt = 'Select both HPV and cytology.'; tone = 'gray'; }
    const note = `**ASCCP Quick Mgmt (2019 simplified)**\n- Age: ${a} · HPV: ${hpv || '—'} · Cytology: ${cyto || '—'}\n- **Recommendation:** ${mgmt}\n- For complex/atypical histories, use the official ASCCP app for full risk-based mgmt.`;
    setResult({ mgmt, tone, note });
  };

  return (
    <div className="space-y-3">
      <Field label="Age (years)"><NumberInput value={age} onChange={setAge} placeholder="30" /></Field>
      <Field label="HPV result">
        <Select value={hpv} onChange={setHpv} options={[
          { value: 'neg', label: 'Negative' },
          { value: 'posOther', label: 'Positive (other high-risk types)' },
          { value: 'pos16', label: 'Positive HPV 16' },
          { value: 'pos1618', label: 'Positive HPV 16 & 18' },
        ]} />
      </Field>
      <Field label="Cytology">
        <Select value={cyto} onChange={setCyto} options={[
          { value: 'nilm', label: 'NILM (normal)' },
          { value: 'ascus', label: 'ASC-US' },
          { value: 'lsil', label: 'LSIL' },
          { value: 'hsil', label: 'HSIL / ASC-H' },
          { value: 'agc', label: 'AGC' },
        ]} />
      </Field>
      <Btn onClick={calculate}>Get Management</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <Note>{result.mgmt}</Note>
            <Note>Simplified ASCCP 2019 guidance. For complex histories use the official ASCCP risk-based app.</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 15. ENDOMETRIAL THICKNESS
// =============================================================================
const EndoThickCalc = ({ onSend }) => {
  const [meno, setMeno] = useState(''); const [bleeding, setBleeding] = useState(''); const [et, setEt] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const v = parseFloat(et); if (!v) return;
    let tone, interp;
    if (meno === 'post' && bleeding === 'yes') {
      if (v <= 4) { tone = 'green'; interp = 'ET ≤ 4 mm in postmenopausal bleeding → low risk of endometrial Ca (~1%). Conservative; biopsy if persistent bleeding.'; }
      else if (v <= 7) { tone = 'orange'; interp = 'ET 5–7 mm with PMB → endometrial sampling (Pipelle / D&C / hysteroscopy-guided biopsy) indicated.'; }
      else { tone = 'red'; interp = 'ET > 7 mm with PMB → MANDATORY hysteroscopy + targeted biopsy. Rule out Ca/hyperplasia.'; }
    } else if (meno === 'post' && bleeding === 'no') {
      if (v < 11) { tone = 'green'; interp = 'Asymptomatic postmenopausal ET < 11 mm → observation. No routine sampling needed.'; }
      else { tone = 'orange'; interp = 'Asymptomatic ET ≥ 11 mm → consider sampling especially if risk factors (tamoxifen, obesity, late menopause).'; }
    } else if (meno === 'pre') {
      const phase = 'Cycle phase determines normal ET (proliferative 4–8 mm, secretory 7–14 mm, peri-ovulatory 6–10 mm).';
      if (v > 16) { tone = 'orange'; interp = `ET > 16 mm in premenopausal woman is abnormal in any phase. Investigate AUB. ${phase}`; }
      else { tone = 'green'; interp = `ET ${v} mm — interpret in cycle context. ${phase} If AUB present → PALM-COEIN workup.`; }
    } else { tone = 'gray'; interp = 'Select menopausal status and bleeding status.'; }
    const note = `**Endometrial Thickness Interpretation**\n- Menopausal: ${meno === 'post' ? 'Postmenopausal' : meno === 'pre' ? 'Premenopausal' : '—'}\n- Bleeding: ${bleeding === 'yes' ? 'Yes' : bleeding === 'no' ? 'No' : '—'}\n- ET: ${v} mm\n- **Interpretation:** ${interp}`;
    setResult({ tone, interp, note });
  };

  return (
    <div className="space-y-3">
      <Field label="Menopausal status">
        <Select value={meno} onChange={setMeno} options={[
          { value: 'pre', label: 'Premenopausal' },
          { value: 'post', label: 'Postmenopausal' },
        ]} />
      </Field>
      <Field label="Bleeding present?">
        <Select value={bleeding} onChange={setBleeding} options={[
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No / asymptomatic' },
        ]} />
      </Field>
      <Field label="Endometrial thickness (mm)"><NumberInput value={et} onChange={setEt} placeholder="6" step="0.1" /></Field>
      <Btn onClick={calculate}>Interpret</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}><Note>{result.interp}</Note></ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 16. OHSS
// =============================================================================
const OHSSCalc = ({ onSend }) => {
  const [amh, setAmh] = useState(''); const [afc, setAfc] = useState(''); const [age, setAge] = useState('');
  const [e2, setE2] = useState(''); const [follicles, setFollicles] = useState(''); const [pcos, setPcos] = useState(false);
  const [result, setResult] = useState(null);

  const calculate = () => {
    let score = 0;
    if (parseFloat(amh) > 3.5) score += 2;
    if (parseInt(afc) > 24) score += 2;
    if (parseFloat(age) < 33) score += 1;
    if (parseFloat(e2) > 3500) score += 2;
    if (parseInt(follicles) > 18) score += 2;
    if (pcos) score += 2;
    let tone, mgmt;
    if (score >= 6) { tone = 'red'; mgmt = 'HIGH OHSS RISK — GnRH agonist trigger preferred (not hCG). Consider freeze-all. Cabergoline 0.5 mg daily ×8 days. Monitor closely.'; }
    else if (score >= 3) { tone = 'orange'; mgmt = 'MODERATE risk — lower hCG trigger (5000 IU) or dual trigger. Cabergoline prophylaxis. Daily monitoring for 7 days post-trigger.'; }
    else { tone = 'green'; mgmt = 'LOW risk — standard hCG trigger acceptable. Routine luteal support.'; }
    const note = `**OHSS Risk Assessment**\n- AMH: ${amh || '—'} · AFC: ${afc || '—'} · Age: ${age || '—'} · E2: ${e2 || '—'} · Follicles ≥11 mm: ${follicles || '—'} · PCOS: ${pcos ? 'Yes' : 'No'}\n- **Score: ${score}**\n- ${mgmt}`;
    setResult({ score, tone, mgmt, note });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="AMH (ng/mL)"><NumberInput value={amh} onChange={setAmh} placeholder="4.0" step="0.1" /></Field>
        <Field label="AFC (total)"><NumberInput value={afc} onChange={setAfc} placeholder="22" /></Field>
        <Field label="Age (years)"><NumberInput value={age} onChange={setAge} placeholder="30" /></Field>
        <Field label="Pre-trigger E2 (pg/mL)"><NumberInput value={e2} onChange={setE2} placeholder="3500" /></Field>
        <Field label="Follicles ≥ 11 mm"><NumberInput value={follicles} onChange={setFollicles} placeholder="18" /></Field>
      </div>
      <Checkbox label="PCOS phenotype" checked={pcos} onChange={setPcos} />
      <Btn onClick={calculate}>Assess OHSS Risk</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="OHSS risk score" v={result.score} tone={result.tone} />
            <Note>{result.mgmt}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 17. UKMEC
// =============================================================================
const UKMECCalc = ({ onSend }) => {
  const matrix = {
    bp140: ['3', '1', '2', '1', '1', '1'],
    bp160: ['4', '2', '3', '2', '1', '2'],
    smoker35: ['4', '1', '1', '1', '1', '1'],
    bmi35: ['3', '1', '1', '1', '1', '1'],
    migraine_aura: ['4', '1', '2', '1', '1', '2'],
    dvt_active: ['4', '3', '3', '3', '1', '3'],
    dvt_past: ['4', '2', '2', '2', '1', '2'],
    breastfeeding_under6w: ['4', '1', '3', '1', '2', '3'],
    diabetes_complications: ['3', '2', '3', '2', '1', '2'],
    breastCa_current: ['4', '4', '4', '4', '1', '4'],
    liver_active: ['4', '3', '3', '3', '1', '3'],
    sle_apla: ['4', '3', '3', '3', '1', '3'],
  };
  const conditions = [
    { value: 'bp140', label: 'BP 140–159 / 90–99' },
    { value: 'bp160', label: 'BP ≥ 160 / 100' },
    { value: 'smoker35', label: 'Smoker ≥ 35 yrs' },
    { value: 'bmi35', label: 'BMI ≥ 35' },
    { value: 'migraine_aura', label: 'Migraine with aura' },
    { value: 'dvt_active', label: 'Current DVT/PE' },
    { value: 'dvt_past', label: 'Past DVT/PE' },
    { value: 'breastfeeding_under6w', label: 'Breastfeeding < 6 weeks PP' },
    { value: 'diabetes_complications', label: 'Diabetes with complications' },
    { value: 'breastCa_current', label: 'Current breast cancer' },
    { value: 'liver_active', label: 'Active liver disease' },
    { value: 'sle_apla', label: 'SLE / APLA positive' },
  ];
  const methods = ['COC', 'POP', 'DMPA', 'Implant', 'Cu-IUD', 'LNG-IUD'];
  const [cond, setCond] = useState('');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const row = matrix[cond]; if (!row) return;
    const rows = methods.map((m, i) => ({ method: m, cat: row[i] }));
    const condLabel = conditions.find((c) => c.value === cond)?.label || cond;
    const note = `**UKMEC Contraception Eligibility**\n- Condition: ${condLabel}\n${rows.map((r) => `- ${r.method}: UKMEC ${r.cat} (${catLabel(r.cat)})`).join('\n')}`;
    setResult({ rows, note });
  };

  const catColor = (c) => c === '1' ? 'green' : c === '2' ? 'yellow' : c === '3' ? 'orange' : 'red';
  const catLabel = (c) => ({ '1': 'No restriction', '2': 'Benefits > risks', '3': 'Risks usually > benefits — avoid unless no alternative', '4': 'Unacceptable risk — DO NOT USE' }[c]);

  return (
    <div className="space-y-3">
      <Field label="Medical condition">
        <Select value={cond} onChange={setCond} options={conditions} />
      </Field>
      <Btn onClick={calculate}>Check Eligibility</Btn>
      {result && (
        <>
          <div className="space-y-2">
            {result.rows.map((r) => (
              <ResultCard key={r.method} tone={catColor(r.cat)}>
                <KV k={r.method} v={`UKMEC ${r.cat}`} tone={catColor(r.cat)} />
                <Note>{catLabel(r.cat)}</Note>
              </ResultCard>
            ))}
          </div>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 18. CAPRINI
// =============================================================================
const CapriniCalc = ({ onSend }) => {
  const [factors, setFactors] = useState({});
  const [weight, setWeight] = useState('');
  const [result, setResult] = useState(null);
  const f = (key, pts) => ({ key, pts });
  const items = [
    f('age4060', 1), f('age6074', 2), f('age75', 3),
    f('minorSurgery', 1), f('majorSurgery', 2), f('arthroplasty', 5),
    f('bmi25', 1), f('pregnancy', 1), f('oc_hrt', 1),
    f('varicose', 1), f('chf', 1), f('mi', 1),
    f('cancer', 2), f('cvc', 2), f('immobile', 2),
    f('priorVTE', 3), f('thrombophilia', 3), f('stroke', 5),
  ];
  const labels = {
    age4060: 'Age 41–60', age6074: 'Age 61–74', age75: 'Age ≥ 75',
    minorSurgery: 'Minor surgery (< 45 min)', majorSurgery: 'Major surgery (≥ 45 min)', arthroplasty: 'Hip/knee arthroplasty',
    bmi25: 'BMI > 25', pregnancy: 'Pregnancy / postpartum', oc_hrt: 'OC / HRT use',
    varicose: 'Varicose veins', chf: 'CHF', mi: 'MI within 1 month',
    cancer: 'Active malignancy', cvc: 'Central venous catheter', immobile: 'Bedrest > 72 hrs',
    priorVTE: 'Prior VTE', thrombophilia: 'Known thrombophilia', stroke: 'Stroke within 1 month',
  };

  const calculate = () => {
    const total = items.reduce((s, it) => s + (factors[it.key] ? it.pts : 0), 0);
    let tier, tone, plan;
    const w = parseFloat(weight);
    if (total >= 5) {
      tier = 'HIGH (≥5)'; tone = 'red';
      plan = w
        ? `Pharmacologic prophylaxis: Enoxaparin ${w > 100 ? '40 mg SC q12h' : '40 mg SC daily'} (or weight-based 0.5 mg/kg q12h). + mechanical (IPC/TEDS).`
        : 'Pharmacologic prophylaxis (LMWH/UFH) + mechanical (IPC + TEDS).';
    } else if (total >= 3) { tier = 'MODERATE (3–4)'; tone = 'orange'; plan = 'Pharmacologic OR mechanical prophylaxis. Enoxaparin 40 mg SC daily if no bleeding risk.'; }
    else if (total >= 1) { tier = 'LOW (1–2)'; tone = 'yellow'; plan = 'Mechanical prophylaxis (early ambulation, IPC).'; }
    else { tier = 'VERY LOW (0)'; tone = 'green'; plan = 'Early ambulation only.'; }
    const picked = items.filter((it) => factors[it.key]).map((it) => labels[it.key]).join(', ') || 'None';
    const note = `**Caprini VTE Risk Assessment**\n- Factors: ${picked}\n- **Score: ${total} → ${tier}**\n- ${plan}`;
    setResult({ total, tier, tone, plan, note });
  };

  return (
    <div className="space-y-3">
      <div className="max-h-48 overflow-y-auto pr-1 border border-gray-200 rounded-lg p-2">
        {items.map((it) => (
          <Checkbox key={it.key} label={`${labels[it.key]} (+${it.pts})`} checked={!!factors[it.key]} onChange={(v) => setFactors((p) => ({ ...p, [it.key]: v }))} />
        ))}
      </div>
      <Field label="Patient weight (kg) — for LMWH dose"><NumberInput value={weight} onChange={setWeight} placeholder="65" /></Field>
      <Btn onClick={calculate}>Calculate Caprini & Dose</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Caprini score" v={result.total} tone={result.tone} />
            <KV k="Risk tier" v={result.tier} tone={result.tone} />
            <Note>{result.plan}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 19. COCKCROFT-GAULT
// =============================================================================
const CockcroftCalc = ({ onSend }) => {
  const [age, setAge] = useState(''); const [weight, setWeight] = useState(''); const [cr, setCr] = useState('');
  const [sex, setSex] = useState('female'); const [result, setResult] = useState(null);

  const calculate = () => {
    const a = parseFloat(age); const w = parseFloat(weight); const c = parseFloat(cr);
    if (!a || !w || !c) return;
    let crcl = ((140 - a) * w) / (72 * c);
    if (sex === 'female') crcl *= 0.85;
    crcl = Math.round(crcl);
    let stage, tone;
    if (crcl >= 90) { stage = 'Normal (≥ 90)'; tone = 'green'; }
    else if (crcl >= 60) { stage = 'Mild reduction (60–89)'; tone = 'yellow'; }
    else if (crcl >= 30) { stage = 'Moderate (30–59) — dose adjust most drugs'; tone = 'orange'; }
    else if (crcl >= 15) { stage = 'Severe (15–29) — avoid nephrotoxics, major dose reduction'; tone = 'red'; }
    else { stage = 'Kidney failure (<15) — most drugs contraindicated or major adjustment'; tone = 'red'; }
    const note = `**Cockcroft-Gault Creatinine Clearance**\n- Age: ${a} · Weight: ${w} kg · Cr: ${c} mg/dL · Sex: ${sex}\n- **CrCl: ${crcl} mL/min**\n- ${stage}`;
    setResult({ crcl, stage, tone, note });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Age (years)"><NumberInput value={age} onChange={setAge} placeholder="40" /></Field>
        <Field label="Weight (kg)"><NumberInput value={weight} onChange={setWeight} placeholder="65" /></Field>
        <Field label="Serum Cr (mg/dL)"><NumberInput value={cr} onChange={setCr} placeholder="0.9" step="0.01" /></Field>
        <Field label="Sex">
          <Select value={sex} onChange={setSex} options={[
            { value: 'female', label: 'Female (×0.85)' },
            { value: 'male', label: 'Male' },
          ]} />
        </Field>
      </div>
      <Btn onClick={calculate}>Calculate CrCl</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="CrCl (Cockcroft-Gault)" v={`${result.crcl} mL/min`} tone={result.tone} />
            <Note>{result.stage}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// 20. BHUTANI BILIRUBIN
// =============================================================================
const BhutaniCalc = ({ onSend }) => {
  const [hours, setHours] = useState(''); const [tsb, setTsb] = useState(''); const [risk, setRisk] = useState('medium');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const h = parseFloat(hours); const t = parseFloat(tsb);
    if (!h || !t) return;
    const interp = (h <= 30) ? [5, 8, 10] : (h <= 54) ? [8, 11, 14] : (h <= 78) ? [10, 13, 16] : [11, 14, 17.5];
    let zone, tone;
    if (t < interp[0]) { zone = 'Low risk zone'; tone = 'green'; }
    else if (t < interp[1]) { zone = 'Low-intermediate risk zone'; tone = 'yellow'; }
    else if (t < interp[2]) { zone = 'High-intermediate risk zone'; tone = 'orange'; }
    else { zone = 'High risk zone — phototherapy threshold'; tone = 'red'; }
    let mgmt;
    const photoThresh = risk === 'high' ? interp[1] : interp[2];
    if (t >= photoThresh + 5) mgmt = 'Consider EXCHANGE TRANSFUSION — escalate immediately.';
    else if (t >= photoThresh) mgmt = 'Start PHOTOTHERAPY. Re-check TSB in 4–6 hrs.';
    else if (t >= photoThresh - 2) mgmt = 'Close monitoring — repeat TSB in 4–8 hrs.';
    else mgmt = 'Routine monitoring with repeat TSB per nursery protocol.';
    const note = `**Bhutani Bilirubin Interpretation**\n- Age: ${h} hrs · TSB: ${t} mg/dL · Risk category: ${risk}\n- Thresholds (40/75/95th): ${interp[0]} / ${interp[1]} / ${interp[2]} mg/dL\n- **Zone: ${zone}**\n- ${mgmt}`;
    setResult({ zone, tone, mgmt, thresholds: interp, note });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Age (hours)"><NumberInput value={hours} onChange={setHours} placeholder="48" /></Field>
        <Field label="Total Serum Bilirubin (mg/dL)"><NumberInput value={tsb} onChange={setTsb} placeholder="12" step="0.1" /></Field>
      </div>
      <Field label="Risk category" hint="based on GA + risk factors">
        <Select value={risk} onChange={setRisk} options={[
          { value: 'low', label: 'Low risk (≥38 wk, well)' },
          { value: 'medium', label: 'Medium (35–37⁶/⁷ wk well OR ≥38 wk + risk factors)' },
          { value: 'high', label: 'High (35–37⁶/⁷ wk + risk factors)' },
        ]} />
      </Field>
      <Btn onClick={calculate}>Interpret TSB</Btn>
      {result && (
        <>
          <ResultCard tone={result.tone}>
            <KV k="Risk zone" v={result.zone} tone={result.tone} />
            <KV k="Thresholds (40/75/95th)" v={`${result.thresholds[0]} / ${result.thresholds[1]} / ${result.thresholds[2]} mg/dL`} tone={result.tone} />
            <Note>{result.mgmt}</Note>
          </ResultCard>
          <SendBtn note={result.note} onSend={onSend} />
        </>
      )}
    </div>
  );
};

// =============================================================================
// REGISTRY & MAIN MODAL
// =============================================================================
const CATEGORIES = [
  {
    name: 'Obstetrics',
    calcs: [
      { id: 'edd', label: 'EDD / GA', Comp: EDDCalc },
      { id: 'bishop', label: 'Bishop Score', Comp: BishopCalc },
      { id: 'fetal', label: 'Fetal Weight (Hadlock)', Comp: FetalWeightCalc },
      { id: 'pe', label: 'Preeclampsia + Aspirin', Comp: PreeclampsiaCalc },
      { id: 'vbac', label: 'VBAC Success', Comp: VBACCalc },
      { id: 'pph', label: 'PPH Risk (CMQCC)', Comp: PPHRiskCalc },
      { id: 'mgso4', label: 'MgSO₄ Eclampsia', Comp: MgSO4Calc },
      { id: 'antid', label: 'Anti-D Dose', Comp: AntiDCalc },
      { id: 'gdm', label: 'GDM Insulin', Comp: GDMInsulinCalc },
    ],
  },
  {
    name: 'Gynaecology',
    calcs: [
      { id: 'rmi', label: 'RMI — Adnexal Mass', Comp: RMICalc },
      { id: 'asccp', label: 'ASCCP HPV+Cyto', Comp: ASCCPCalc },
      { id: 'endothick', label: 'Endometrial Thickness', Comp: EndoThickCalc },
      { id: 'ukmec', label: 'UKMEC Contraception', Comp: UKMECCalc },
    ],
  },
  {
    name: 'Fertility',
    calcs: [
      { id: 'amh', label: 'AMH Interpretation', Comp: AMHCalc },
      { id: 'bmi', label: 'BMI & Fertility', Comp: BMICalc },
      { id: 'ohss', label: 'OHSS Risk', Comp: OHSSCalc },
    ],
  },
  {
    name: 'Safety / Dosing',
    calcs: [
      { id: 'caprini', label: 'Caprini VTE + LMWH', Comp: CapriniCalc },
      { id: 'crcl', label: 'CrCl (Cockcroft-Gault)', Comp: CockcroftCalc },
    ],
  },
  {
    name: 'Neonatal',
    calcs: [
      { id: 'apgar', label: 'APGAR', Comp: APGARCalc },
      { id: 'bili', label: 'Bhutani Bilirubin', Comp: BhutaniCalc },
    ],
  },
];

const ALL_CALCS = CATEGORIES.flatMap((c) => c.calcs);

const CalculatorModal = ({ isOpen, onClose, onSendToChat }) => {
  const [activeCat, setActiveCat] = useState(CATEGORIES[0].name);
  const [activeCalc, setActiveCalc] = useState('edd');
  const current = ALL_CALCS.find((c) => c.id === activeCalc);
  const Active = current?.Comp;

  // Wrap the send callback to close the modal after dispatching
  const handleSend = onSendToChat
    ? (note) => {
        onSendToChat(note);
        onClose && onClose();
      }
    : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="calculator-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-raspberry-800">
            <Calculator className="w-5 h-5" />
            OB-GYN Calculators
            <span className="ml-2 text-xs font-normal text-gray-500">{ALL_CALCS.length} tools</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-1.5 mb-3 sticky top-0 bg-white pb-2 z-10" data-testid="calc-categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.name}
              onClick={() => {
                setActiveCat(cat.name);
                setActiveCalc(cat.calcs[0].id);
              }}
              data-testid={`calc-cat-${cat.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                activeCat === cat.name
                  ? 'bg-raspberry-700 text-white border-raspberry-700'
                  : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="calc-list">
          {(CATEGORIES.find((c) => c.name === activeCat)?.calcs || []).map((calc) => (
            <button
              key={calc.id}
              onClick={() => setActiveCalc(calc.id)}
              data-testid={`calc-pick-${calc.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                activeCalc === calc.id
                  ? 'bg-raspberry-50 text-raspberry-700 border border-raspberry-300'
                  : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100'
              }`}
            >
              {calc.label}
            </button>
          ))}
        </div>

        <div key={activeCalc} className="pt-2 border-t border-gray-100">
          {Active ? <Active onSend={handleSend} /> : <p className="text-sm text-gray-500">Select a calculator above.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CalculatorModal;
